import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { submitArticle, getCategories } from '../services/api';
import { useDocumentTitle } from '../utils/useDocumentTitle';

function SubmitArticle() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    description: '',
    author: '',
    category: '',
    image: ''
  });
  const [editorType, setEditorType] = useState('markdown');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [imagePreviewError, setImagePreviewError] = useState(false);

  // Set document title
  useDocumentTitle('Submit Article - RealSoft Newsletter');

  // Fetch categories when component mounts
  useEffect(() => {
    async function fetchCategories() {
      try {
        const response = await getCategories();
        if (response.success && response.data) {
          setCategories(response.data);
        } else {
          setError('Failed to load categories. Please try again later.');
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories. Please try again later.');
      }
    }

    fetchCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    // Reset image preview error when URL changes
    if (name === 'image') {
      setImagePreviewError(false);
    }
  };

  // Handler for ReactQuill
  const handleQuillChange = (content) => {
    setFormData(prevState => ({
      ...prevState,
      content: content
    }));
  };

  const handleImageError = () => {
    setImagePreviewError(true);
  };

  // Helper function to detect if content contains HTML tags
  const containsHTML = (content) => {
    const htmlRegex = /<[^>]*>/;
    return htmlRegex.test(content);
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const switchEditor = (type) => {
    if (editorType !== type) {
      // Optional: Convert content between formats or clear it
      // For simplicity, we'll clear it when switching.
      // A more advanced implementation could try to convert Markdown to HTML (and vice-versa, which is harder).
      setFormData(prevState => ({ ...prevState, content: '' }));
      setEditorType(type);
      setShowPreview(false); // Hide preview when switching editor
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Make sure we have a valid category ID
      if (!formData.category) {
        throw new Error('Please select a category');
      }

      const articlePayload = {
        ...formData,
        contentType: editorType
      };

      const response = await submitArticle(articlePayload);

      if (response) {
        setSuccess(true);
        setFormData({
          title: '',
          content: '',
          description: '',
          author: '',
          category: '',
          image: ''
        });
        setShowPreview(false);
        setEditorType('markdown'); // Reset to default editor
      } else {
        throw new Error('Failed to submit article');
      }
    } catch (err) {
      console.error('Error submitting article:', err);
      setError(err.message || 'An error occurred while submitting your article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Quill editor modules
  const quillModules = {
    toolbar: [
      [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
      [{size: []}],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, 
       {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image', 'video'], // 'image' and 'video' might require server-side handling for uploads
      ['clean'],
      [{ 'color': [] }, { 'background': [] }], // Font color and background color
      [{ 'align': [] }], // Text alignment
      ['code-block'] // Code block
    ],
  };

  if (success) {
    return (
      <>
        <Header />
        <div className="container section">
          <div className="success-message">
            <h2>Article Submitted Successfully!</h2>
            <p>Thank you for your submission. Your article will be reviewed by our editorial team.</p>
            <div className="button-group mt-6 flex gap-4">
              <button
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                onClick={() => {
                  setSuccess(false);
                }}
              >
                Submit Another Article
              </button>
              <button
                className="px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors border border-gray-300"
                onClick={() => navigate('/')}
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container section submit-article-page">
        <h1>Submit an Article</h1>
        <p className="subtitle">
          Share your knowledge and insights with our readers. All submissions will be reviewed before publishing.
        </p>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2 md:col-span-1">
              <div className="form-group">
                <label htmlFor="title" className="text-gray-700 font-medium">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-2 border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description" className="text-gray-700 font-medium">Description/Summary (Max 200 characters) *</label>
                <div className="relative">
                  <textarea
                    id="description"
                    name="description"
                    required
                    maxLength={200}
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border-2 border-gray-400 bg-gray-50 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-4"
                    placeholder="Write a brief summary of your article here. This will appear in article previews and search results."
                  />
                  <span className="absolute bottom-2 right-2 text-xs text-gray-500">
                    {formData.description.length}/200
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">A concise summary helps readers understand what your article is about.</p>
              </div>

              <div className="form-group">
                <label htmlFor="category" className="text-gray-700 font-medium">Select Category *</label>
                <div className="relative">
                  <select
                    id="category"
                    name="category"
                    required
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border-2 border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white pl-4 pr-10 py-2 appearance-none"
                  >
                    <option value="">-- Choose a category --</option>
                    {categories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Choose the most relevant category for your article</p>
              </div>

              <div className="form-group">
                <label htmlFor="author" className="text-gray-700 font-medium">Author Name *</label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  required
                  value={formData.author}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-2 border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="form-group">
                <label htmlFor="image" className="text-gray-700 font-medium">Featured Image URL</label>
                <div className="mt-1">
                  <input
                    type="url"
                    id="image"
                    name="image"
                    placeholder="https://example.com/your-article-image.jpg"
                    value={formData.image}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border-2 border-gray-400 bg-white shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add a URL to a high-quality image (recommended size: 1200×630 pixels) that represents your article.
                    This image will be displayed as the thumbnail in listings and at the top of your article.
                  </p>
                </div>

                {formData.image && (
                  <div className="image-preview mt-3">
                    <p className="text-sm text-gray-700 mb-1">Image Preview:</p>
                    {!imagePreviewError ? (
                      <img
                        src={formData.image}
                        alt="Preview"
                        className="max-h-40 object-cover rounded-lg border-2 border-gray-400 shadow-sm"
                        onError={handleImageError}
                      />
                    ) : (
                      <div className="invalid-image-url p-3 bg-red-50 rounded-lg border-2 border-red-300">
                        <p className="text-red-500 text-sm">Unable to load image from this URL. Please check the URL and try again.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            </div>
            <div className="col-span-2 md:col-span-1 form-group">
              <label className="text-gray-700 font-medium">Content *</label>
              <div className="editor-controls my-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => switchEditor('markdown')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${editorType === 'markdown' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Markdown
                </button>
                <button
                  type="button"
                  onClick={() => switchEditor('richtext')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${editorType === 'richtext' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Rich Text (WYSIWYG)
                </button>
              </div>

              {editorType === 'markdown' ? (
                <textarea
                  id="content"
                  name="content"
                  required
                  rows={15}
                  value={formData.content}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-2 border-gray-400 bg-gray-50 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-4"
                  placeholder="Write your article content here using Markdown or HTML..."
                />
              ) : (
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={handleQuillChange}
                  modules={quillModules}
                  className="bg-white rounded-lg shadow-sm editor-height-large"
                  placeholder="Write your article content here..."
                />
              )}
              <p className="text-xs text-gray-500 mt-1">
                {editorType === 'markdown' ? "Use Markdown or HTML for formatting. " : "Use the toolbar for formatting. "}
                <button type="button" onClick={togglePreview} className="text-blue-600 hover:underline">
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </p>
            </div>


          {showPreview && (
            <div className="preview-area mt-6 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Content Preview</h3>
              {editorType === 'markdown' ? (
                containsHTML(formData.content) ? (
                  <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: formData.content }} />
                ) : (
                  <ReactMarkdown className="prose max-w-none">{formData.content}</ReactMarkdown>
                )
              ) : (
                <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: formData.content }} />
              )}
            </div>
          )}

          <div className="form-actions mt-8 text-right">
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Article'}
            </button>
          </div>
        </form>
      </div>
      <Footer />
    </>
  );
}

export default SubmitArticle; 